import { useCallback, useEffect, useRef } from "react";
import { io } from "socket.io-client";

export const useMatchSocket = ({ detail, user, parmas, Show, handlers }) => {
  const socketRef = useRef(null);
  const isManualDisconnectRef = useRef(false);
  const checkRefreshRef = useRef(false);
  const reconnectTimerRef = useRef(null); // ✅ Track timer to cancel it

  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const disconnect = useCallback(() => {
    // ✅ Cancel any pending reconnect before disconnecting
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (socketRef.current) {
      isManualDisconnectRef.current = true;
      socketRef.current.removeAllListeners(); // ✅ Remove listeners BEFORE disconnect
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (checkRefreshRef.current) return;
    checkRefreshRef.current = true;

    // ✅ Store timer ref so it can be cancelled
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      checkRefreshRef.current = false;
      connect();
    }, 2000);
  }, []);  // ✅ Removed `connect` from deps — avoids circular ref

  const connect = useCallback(() => {
    if (!detail?.eventId) return;

    // ✅ If socket is currently connecting (not yet open), don't restart
    if (
      socketRef.current &&
      !socketRef.current.connected &&
      socketRef.current.io?.readyState === "opening"
    ) {
      return;
    }

    disconnect();
    isManualDisconnectRef.current = false;
    checkRefreshRef.current = false;

    const userId = user?.user?._id ?? 112233;
    const baseUrl = detail?.socketUrl ?? process.env.REACT_APP_SOCKET;

    const url = user
      ? `${baseUrl}?token=${userId}&userType=front&eventId=${detail.eventId}`
      : `${baseUrl}?user_id=${userId}&eventId=${detail.eventId}`;

    const socket = io(url, {
      transports: ["websocket"],
      forceNew: true,
      reconnection: false, // ✅ Disable socket.io's built-in reconnect — we handle it
    });
    socketRef.current = socket;

    socket.on("connect_error", (err) => {
      console.warn("[MatchSocket] connect_error:", err.message);
      // ✅ Only reconnect if this is still the active socket
      if (socketRef.current === socket) {
        scheduleReconnect();
      }
    });

    socket.on("error", (err) => {
      console.warn("[MatchSocket] error:", err);
    });

    socket.on("listenBetFairOdds", (...args) =>
      handlersRef.current?.onBetfairOdds?.(...args)
    );

    socket.on("listenBookmakerOdds", (...args) =>
      handlersRef.current?.onBookmakerOdds?.(...args)
    );

    if (detail?.gameType === "cricket" && Show?.fancy) {
      socket.on("listenDiamondFancy", (...args) =>
        handlersRef.current?.onDiamondFancy?.(...args)
      );
    }

    if (Show?.sportsBook) {
      socket.on("listenPremiumFancy", (...args) =>
        handlersRef.current?.onPremiumFancy?.(...args)
      );
      if (user) {
        socket.emit("setPremiumFancy", {
          user_id: userId,
          eventId: parmas.eventId,
        });
      }
    }

    socket.on("disconnect", (reason) => {
      console.warn("[MatchSocket] disconnect reason:", reason);

      // ✅ Don't reconnect if we intentionally disconnected
      if (isManualDisconnectRef.current) {
        isManualDisconnectRef.current = false;
        return;
      }

      // ✅ Only reconnect if this is still the active socket
      if (socketRef.current === socket) {
        scheduleReconnect();
      }
    });

    handlersRef.current?.onSocketCreated?.(socket);
  }, [
    detail?.eventId,
    detail?.gameType,
    detail?.socketUrl,
    user,
    Show?.fancy,
    Show?.sportsBook,
    disconnect,
    scheduleReconnect,
  ]);

  useEffect(() => {
    if (detail?.eventId) connect();
    return disconnect;
  }, [detail?.eventId]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        disconnect();
      } else {
        connect();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [connect, disconnect]);

  return { disconnectSocket: disconnect, reconnectSocket: connect };
};